import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Mail, Trash2, Download, Tag, X } from 'lucide-react';
import { toast } from 'sonner';
import { getSubscribers, deleteSubscriber, downloadCsv, setSubscriberGroups, bulkSubscriberGroup } from '../../lib/api';
import { NewsletterComposer, GROUPS } from './NewsletterComposer';
import { AddSubscribers } from './AddSubscribers';

const SOURCE_LABEL = { website: 'Website', admin: 'Added by admin', bulk: 'Bulk paste', import: 'File import', member: 'Member profile' };
const FILTERS = [['all', 'All'], ...GROUPS, ['unsubscribed', 'Unsubscribed']];
const chip = (on) => `font-sans-ah text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? 'bg-[#4a9cd4] border-[#4a9cd4] text-white' : 'border-neutral-300 text-[#888] hover:border-[#4a9cd4] hover:text-[#4a9cd4]'}`;

const matches = (s, f) => (f === 'all' ? true : f === 'unsubscribed' ? s.unsubscribed : !s.unsubscribed && (s.groups || []).includes(f));

function SubscriberRow({ s, selected, onSelect, onToggleGroup, onRemove }) {
  return (
    <div data-testid={`subscriber-row-${s.id}`} className={`flex flex-wrap items-center gap-3 bg-white border rounded-sm p-3.5 ${selected ? 'border-[#4a9cd4]' : 'border-neutral-100'} ${s.unsubscribed ? 'opacity-60' : ''}`}>
      <input type="checkbox" checked={selected} onChange={() => onSelect(s.id)} aria-label={`Select ${s.email}`} data-testid={`select-subscriber-${s.id}`} />
      <Mail size={16} className="text-[#4a9cd4] shrink-0" />
      <div className="flex-1 min-w-[200px]">
        <p className="font-sans-ah text-[14px] text-[#2f2f2f] truncate">{s.email}{s.name ? <span className="text-[#999]"> — {s.name}</span> : null}</p>
        <p className="font-sans-ah text-[12px] text-[#999]">
          {(s.created_at || '').slice(0, 10)} • {SOURCE_LABEL[s.source] || 'Website'}
          {s.unsubscribed && <span data-testid={`unsubscribed-badge-${s.id}`} className="ml-2 text-red-500 uppercase tracking-wide-ah text-[10px]">Unsubscribed</span>}
        </p>
      </div>
      <div className="flex gap-1.5">
        {GROUPS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => onToggleGroup(s, k)} data-testid={`subscriber-${s.id}-group-${k}`} className={chip((s.groups || []).includes(k))}>{label}</button>
        ))}
      </div>
      <button onClick={() => onRemove(s.id)} aria-label="Remove subscriber" data-testid={`remove-subscriber-${s.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-sm"><Trash2 size={17} /></button>
    </div>
  );
}

function BulkBar({ count, onApply, onClear }) {
  const [group, setGroup] = useState('members');
  return (
    <div className="sticky top-[90px] z-10 flex flex-wrap items-center gap-3 bg-[#1f2c33] text-white rounded-sm px-4 py-3 mb-3" data-testid="subscribers-bulk-bar">
      <Tag size={16} className="text-[#8fc0e2]" />
      <span className="font-sans-ah text-[13px]">{count} selected</span>
      <select value={group} onChange={(e) => setGroup(e.target.value)} aria-label="Group" data-testid="bulk-group-select" className="text-[#2f2f2f] font-sans-ah text-[13px] rounded-sm px-2 py-1.5">
        {GROUPS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>
      <button onClick={() => onApply(group, 'add')} data-testid="bulk-group-add-btn" className="font-sans-ah text-[12px] tracking-wide-ah px-3 py-1.5 rounded-sm bg-[#4a9cd4] hover:bg-[#3b8bc2]">ADD TO GROUP</button>
      <button onClick={() => onApply(group, 'remove')} data-testid="bulk-group-remove-btn" className="font-sans-ah text-[12px] tracking-wide-ah px-3 py-1.5 rounded-sm border border-white/30 hover:bg-white/10">REMOVE FROM GROUP</button>
      <button onClick={onClear} aria-label="Clear selection" data-testid="bulk-clear-btn" className="ml-auto p-1.5 hover:bg-white/10 rounded-sm"><X size={16} /></button>
    </div>
  );
}

export function Subscribers() {
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const load = useCallback(() => getSubscribers().then(setSubs).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return subs.filter((s) => matches(s, filter) && (!term || `${s.email} ${s.name || ''}`.toLowerCase().includes(term)));
  }, [subs, q, filter]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map(([k]) => [k, subs.filter((s) => matches(s, k)).length])), [subs]);
  const activeCount = subs.filter((s) => !s.unsubscribed).length;

  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allShownSelected = shown.length > 0 && shown.every((s) => selected.has(s.id));
  const toggleAll = () => setSelected(allShownSelected ? new Set() : new Set(shown.map((s) => s.id)));

  const toggleGroup = async (s, g) => {
    const groups = (s.groups || []).includes(g) ? s.groups.filter((x) => x !== g) : [...(s.groups || []), g];
    setSubs((prev) => prev.map((x) => (x.id === s.id ? { ...x, groups } : x)));
    try { await setSubscriberGroups(s.id, groups); } catch { toast.error('Could not update groups'); load(); }
  };
  const applyBulk = async (group, action) => {
    try {
      const r = await bulkSubscriberGroup([...selected], group, action);
      toast.success(`${r.updated} subscriber${r.updated === 1 ? '' : 's'} updated`);
      setSelected(new Set()); load();
    } catch { toast.error('Could not update groups'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try { await deleteSubscriber(id); toast.success('Removed'); load(); } catch { toast.error('Could not remove subscriber'); }
  };
  const copyShown = () => {
    navigator.clipboard.writeText(shown.filter((s) => !s.unsubscribed).map((s) => s.email).join(', '));
    toast.success('Emails copied to clipboard');
  };

  return (
    <div>
      <NewsletterComposer subscribers={subs} />
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h2 className="font-serif-ah text-[26px] text-[#2f2f2f]" data-testid="subscribers-heading">Newsletter Subscribers ({activeCount} active)</h2>
        {subs.length > 0 && (
          <div className="flex gap-2">
            <button onClick={() => downloadCsv('subscribers')} data-testid="export-subscribers-btn" className="font-sans-ah text-[12px] tracking-wide-ah px-4 py-3 rounded-sm border border-neutral-300 text-[#555] hover:bg-white flex items-center gap-2"><Download size={15} /> EXPORT CSV</button>
            <button onClick={copyShown} data-testid="copy-subscribers-btn" className="btn-ah-solid font-sans-ah text-[12px] px-5 py-3 rounded-sm flex items-center gap-2"><Mail size={15} /> COPY EMAILS</button>
          </div>
        )}
      </div>
      <AddSubscribers onDone={load} />
      {subs.length === 0 ? <p className="font-sans-ah text-[#888]">No subscribers yet.</p> : (
        <>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="subscriber-filters">
            {FILTERS.map(([k, label]) => (
              <button key={k} onClick={() => { setFilter(k); setSelected(new Set()); }} data-testid={`subscriber-filter-${k}`}
                className={`font-sans-ah text-[12px] px-3.5 py-2 rounded-full border transition-colors ${filter === k ? 'bg-[#1f2c33] border-[#1f2c33] text-white' : 'border-neutral-300 text-[#555] hover:border-[#4a9cd4]'}`}>
                {label} ({counts[k]})
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 mb-3">
            <label className="flex items-center gap-2 font-sans-ah text-[13px] text-[#555] shrink-0">
              <input type="checkbox" checked={allShownSelected} onChange={toggleAll} data-testid="select-all-subscribers" /> Select all shown
            </label>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name" aria-label="Search subscribers" data-testid="subscribers-search-input"
              className="flex-1 px-4 py-2.5 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[14px] focus:outline-none focus:border-[#4a9cd4]" />
          </div>
          {selected.size > 0 && <BulkBar count={selected.size} onApply={applyBulk} onClear={() => setSelected(new Set())} />}
          <div className="space-y-2" data-testid="subscribers-list">
            {shown.map((s) => <SubscriberRow key={s.id} s={s} selected={selected.has(s.id)} onSelect={toggleSelect} onToggleGroup={toggleGroup} onRemove={remove} />)}
            {shown.length === 0 && <p className="font-sans-ah text-[13px] text-[#999] py-4">No subscribers match.</p>}
          </div>
        </>
      )}
    </div>
  );
}
